import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload, Loader2, X } from 'lucide-react'
import { supabase } from '../../../lib/supabaseClient'
import { compressImage } from '../../../lib/compressImage'
import { useAuth } from '../../../contexts/AuthContext'
import { cn } from '../../../lib/utils'

const BUCKET = 'exercise-photos'
// All extensions we may have stored for an exercise across uploads.
// Whenever we save a new file under one of them, we delete the rest so
// the row's photo path always points at the freshest blob.
const KNOWN_EXTENSIONS = ['jpg', 'gif', 'png', 'webp']

// Compresses + uploads to the exercise-photos bucket. Returns a storage path
// (`{userId}/{exerciseId}.{ext}`) the parent can save on the exercises row.
//
// GIF behaviour: GIFs are uploaded as-is (no canvas compression) because the
// canvas pipeline only captures the first frame and we want animation
// preserved. Everything else goes through compressImage which composites on
// a white background — fixes the "transparent PNG on dark theme = invisible
// illustration" issue.
//
// Props:
// - exerciseId  string (required) — used as the filename, ensures upserts
//                                   replace rather than accumulate.
// - currentUrl  string|null      — current public URL (if any) to render
// - onUploaded  (path) => void   — fires with the stored path on success
// - onCleared   () => void       — fires when user removes the photo
export default function PhotoUploader({ exerciseId, currentUrl, onUploaded, onCleared }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const fileInput = useRef(null)
  const [previewUrl, setPreviewUrl] = useState(currentUrl || null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const handleSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !user || !exerciseId) return
    setError(null)
    setUploading(true)
    try {
      const isGif = file.type === 'image/gif'
      let blob
      let ext
      let contentType
      if (isGif) {
        // Skip compression entirely — running through canvas would freeze
        // the animation at frame one. Storage gets the raw GIF.
        blob = file
        ext = 'gif'
        contentType = 'image/gif'
      } else {
        blob = await compressImage(file, { background: '#ffffff' })
        ext = 'jpg'
        contentType = 'image/jpeg'
      }

      const localPreview = URL.createObjectURL(blob)
      setPreviewUrl(localPreview)

      const path = `${user.id}/${exerciseId}.${ext}`
      const { error: uploadErr } = await supabase
        .storage
        .from(BUCKET)
        .upload(path, blob, { contentType, upsert: true })
      if (uploadErr) {
        setError(uploadErr.message)
        return
      }
      // Remove any previously-saved files under a different extension so
      // the bucket doesn't accumulate stale uploads each time the user
      // swaps between a JPG and a GIF.
      const stalePaths = KNOWN_EXTENSIONS
        .filter((e2) => e2 !== ext)
        .map((e2) => `${user.id}/${exerciseId}.${e2}`)
      if (stalePaths.length > 0) {
        await supabase.storage.from(BUCKET).remove(stalePaths)
      }
      onUploaded?.(path)
    } catch (err) {
      setError(err?.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const clear = () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    onCleared?.()
  }

  if (previewUrl) {
    return (
      <div className="relative">
        <img
          src={previewUrl}
          alt={t('workout.exercises.form.photo')}
          className="w-full max-h-56 object-contain rounded-lg border border-border bg-white"
        />
        <button
          type="button"
          onClick={clear}
          className="absolute top-2 end-2 p-2 rounded-full bg-black/60 hover:bg-red-500 text-white transition-colors"
          aria-label="Remove photo"
        >
          <X className="w-4 h-4" />
        </button>
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
            <Loader2 className="w-6 h-6 animate-spin text-white" />
          </div>
        )}
      </div>
    )
  }

  return (
    <label
      className={cn(
        'flex flex-col items-center justify-center h-32 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
        'border-input bg-background hover:bg-accent/50',
        uploading && 'opacity-60 cursor-wait'
      )}
    >
      {uploading ? (
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mb-2" />
      ) : (
        <Upload className="w-6 h-6 text-muted-foreground mb-2" />
      )}
      <span className="text-sm text-muted-foreground">
        {uploading ? t('workout.uploading') : t('workout.exercises.form.uploadPhoto')}
      </span>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        onChange={handleSelect}
        disabled={uploading}
        className="hidden"
      />
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </label>
  )
}
